import { search as jmespathCommunitySearch } from '@jmespath-community/jmespath';
import { JSONValue } from '@jmespath-community/jmespath/dist/types/JSON.type';
import chalk from 'chalk';
import { dequal } from 'dequal';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Sole allowable use of jmespath
import { search as jmespathSearch } from 'jmespath';
import { debounce } from 'lodash';
import { coerceErrorToString } from './ErrorUtils';
import { AWLogCreator, generateLogger, GenStatV2, LoggerOptions } from './logger';
import { AW_URL_QUERY_PARAM, getAwUrlParam, getDebugOnFromUrl } from './UrlUtils';

const createLoggerWithOptions = <T extends Partial<GenStatV2>>(logger: AWLogCreator<T>) => {
  const getLoggerOptions = (): LoggerOptions => {
    const pkg = getAwUrlParam(AW_URL_QUERY_PARAM.PKG) || 'Package not found';
    const application = 'AWAdvisor'; // TODO: get app name?

    return {
      key5: pkg,
      subModule: application,
      tagDef: 'ARWMTag',
    };
  };
  return generateLogger(logger, getLoggerOptions);
};

function jmespathSearchMismatchLogCreator({ attrib1 }: Pick<GenStatV2, 'attrib1'> = {}): Partial<GenStatV2> {
  return {
    key2: 'JmespathSearchMismatch',
    attrib1,
  };
}

const logJmespathSearchMismatch = debounce(createLoggerWithOptions(jmespathSearchMismatchLogCreator), 1000);

function jmespathSearchErrorLogCreator({
  key2,
  attrib1,
  attrib2,
}: Pick<GenStatV2, 'key2' | 'attrib1' | 'attrib2'> = {}): Partial<GenStatV2> {
  return {
    key2,
    attrib1,
    attrib2,
  };
}

const logJmespathSearchError = debounce(createLoggerWithOptions(jmespathSearchErrorLogCreator), 1000);

export function search(data: unknown, expression: string): unknown | undefined;
export function search<Result = unknown>(data: unknown, expression: string): Result | undefined;
export function search<Result, Data extends {}>(data: Data, expression: string): Result | undefined;
export function search<Result = unknown, Data = unknown>(data: Data, expression: string): Result | undefined {
  let jmespathResult: Result | undefined;
  let communityResult: Result | undefined;
  const errors = [];

  try {
    jmespathResult = jmespathSearch(data, expression) as Result;
  } catch (e) {
    errors.push(e);
    logJmespathSearchError({
      key2: 'JmespathSearchError',
      attrib1: expression,
      attrib2: coerceErrorToString(e),
    });
  }

  try {
    communityResult = jmespathCommunitySearch(data as JSONValue, expression, {
      enable_legacy_literals: true,
    }) as Result;
  } catch (e) {
    errors.push(e);
    logJmespathSearchError({
      key2: 'JmespathCommunitySearchError',
      attrib1: expression,
      attrib2: coerceErrorToString(e),
    });
  }

  /**
   * This will print debug message in browser console if debugOn=true will be passed as url param
   * Example: http://localhost:3000/?id=1111&login=s4manton&viewas=Carol%20Becker&analysisDate=2022-12-30&pkg=AW_DEMO&debugOn=true#/proposals
   */
  const debugOn = getDebugOnFromUrl();
  /* istanbul ignore next */
  Boolean(debugOn) &&
    /* eslint-disable no-console -- allow for viewing jmespath search object and expression */
    console.debug(chalk.green('Debug is ON: JMESPath Search => '), {
      data,
      expression,
      result: communityResult || jmespathResult,
    });

  if (jmespathResult !== undefined && communityResult !== undefined && !dequal(jmespathResult, communityResult)) {
    Boolean(debugOn) &&
      console.warn(
        new Error(`jmespath search results are not equal for expression: ${expression}
        ${String(jmespathResult)}
        ${String(communityResult)}`)
      );

    logJmespathSearchMismatch({
      attrib1: expression,
    });

    return jmespathResult;
  }

  if (errors.length > 0 && jmespathResult === undefined && communityResult === undefined) {
    throw new Error(errors.join('\n'));
  }
  return communityResult || jmespathResult;
}
